/**
 * useWebSocket.js
 * Shared singleton WebSocket hook connecting to the Python WS Bridge (ws_bridge.py).
 *
 * Key features:
 *  - Single WS connection shared by all components (module-level singleton)
 *  - sessionStorage cache for the last good global_update / ticker message:
 *    on page refresh the cached payload is fired to handlers IMMEDIATELY
 *    (t=0ms), so Three.js and workers can start rendering before the WS
 *    even finishes connecting (~500ms later).
 *  - Reconnect delay kept at 2s (was 3s) for a slightly faster recovery.
 */
import { useEffect, useRef } from 'react';

// ─── sessionStorage cache ─────────────────────────────────────────────────────
const CACHE_TTL  = 5 * 60_000; // 5 minutes — stale after tab sits idle
const CACHE_PFX  = 'cws_';     // 'cyber_ws_<type>'

const WsCache = {
  get(type) {
    try {
      const raw = sessionStorage.getItem(CACHE_PFX + type);
      if (!raw) return null;
      const { msg, ts } = JSON.parse(raw);
      return Date.now() - ts < CACHE_TTL ? msg : null;
    } catch { return null; }
  },
  set(type, msg) {
    try {
      sessionStorage.setItem(CACHE_PFX + type, JSON.stringify({ msg, ts: Date.now() }));
    } catch { /* quota exceeded — ignore */ }
  },
};

// ─── Module-level singleton ───────────────────────────────────────────────────
let _ws                = null;
let _listeners         = {};   // { messageType: Set<callback> }
let _reconnectTimer    = null;
let _reconnectAttempts = 0;    // for exponential backoff (reset on a healthy open)
let _url               = null;
let _connected         = false;
let _lastDataTs        = 0;    // when the last real data frame arrived
const _connectionListeners = new Set();

/** Epoch ms of the last non-ping frame from the bridge (0 = never). Used by the
 *  degraded-data banner to detect a silently stalled pipeline. */
export function getLastDataTs() { return _lastDataTs; }

// Types we bother caching (ping is ephemeral, skip it)
const CACHEABLE = new Set(['global_update', 'ticker', 'top_symbols']);

// Subscribers are isolated from each other on purpose. `forEach` propagates the
// first exception and silently skips every remaining handler, so one broken
// consumer starves all the others: on 04.08.2026 a ReferenceError inside
// useLiveWalls' ingest left QuantScreener's handler unreached, and because that
// handler is what sets the connection flag, the UI sat on "CONNECTING TO
// UPLINK" forever with no data — from a bug in a completely different file.
// One logged error beats a total blackout.
function safeCall(h, msg) {
  try {
    h(msg);
  } catch (e) {
    console.error(`[WS] handler for "${msg?.type}" threw:`, e);
  }
}

function dispatch(msg) {
  const handlers = _listeners[msg.type];
  if (handlers) handlers.forEach(h => safeCall(h, msg));
  const all = _listeners['*'];
  if (all) all.forEach(h => safeCall(h, msg));
}

function getOrCreateWS(url) {
  _url = url;
  if (_ws && (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING)) {
    return _ws;
  }

  _ws = new WebSocket(url);

  _ws.onopen = () => {
    _connected = true;
    _reconnectAttempts = 0;   // healthy again — reset backoff
    _connectionListeners.forEach(cb => { try { cb(true); } catch (e) { console.error('[WS] status listener threw:', e); } });
    console.log('[WS] Connected to bridge');
  };

  _ws.onmessage = (ev) => {
    let msg;
    try { msg = JSON.parse(ev.data); } catch { return; }

    // Keep cache warm with every live message
    if (CACHEABLE.has(msg.type)) WsCache.set(msg.type, msg);
    if (msg.type !== 'ping') _lastDataTs = Date.now();

    dispatch(msg);
  };

  _ws.onclose = () => {
    _connected = false;
    _connectionListeners.forEach(cb => { try { cb(false); } catch (e) { console.error('[WS] status listener threw:', e); } });
    // Exponential backoff (2s → 30s cap) so a long bridge outage isn't hammered
    // every 2s forever; resets to 2s on the next healthy open.
    const delay = Math.min(30_000, 2000 * 2 ** _reconnectAttempts);
    _reconnectAttempts += 1;
    console.warn(`[WS] Disconnected — reconnecting in ${delay}ms`);
    clearTimeout(_reconnectTimer);
    _reconnectTimer = setTimeout(() => getOrCreateWS(_url), delay);
  };

  _ws.onerror = (e) => console.error('[WS] Error:', e);

  return _ws;
}

// ─── Public hooks ─────────────────────────────────────────────────────────────

/**
 * Subscribe to one or more WS message types.
 * On first mount the hook immediately replays the last cached message for each
 * requested type (if one exists and is < 5 min old), so components can render
 * meaningful data before the WebSocket finishes reconnecting.
 */
export function useWebSocket(wsUrl, types, handler) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    getOrCreateWS(wsUrl);

    const typeList = Array.isArray(types) ? types : [types];
    const wrapped  = (msg) => handlerRef.current(msg);

    typeList.forEach(type => {
      if (!_listeners[type]) _listeners[type] = new Set();
      _listeners[type].add(wrapped);

      // ── Instant cache replay ───────────────────────────────────────────────
      // Fire the last known payload immediately (next microtask) so the
      // component renders with real data while the WS is still connecting.
      if (CACHEABLE.has(type)) {
        const cached = WsCache.get(type);
        if (cached) {
          // Mark as cached so consumers can optionally dim stale data
          const replayMsg = { ...cached, _fromCache: true };
          // setTimeout 0 → after React finishes this render cycle
          setTimeout(() => {
            // Only fire if handler is still mounted
            if (_listeners[type]?.has(wrapped)) {
              handlerRef.current(replayMsg);
            }
          }, 0);
        }
      }
    });

    return () => {
      typeList.forEach(type => _listeners[type]?.delete(wrapped));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl, JSON.stringify(types)]);
}

/**
 * Subscribe to WS connection status changes.
 * Fires immediately with the current status.
 */
export function useWSStatus(wsUrl, onStatusChange) {
  const cbRef = useRef(onStatusChange);
  cbRef.current = onStatusChange;

  useEffect(() => {
    getOrCreateWS(wsUrl);
    const wrapped = (s) => cbRef.current(s);
    _connectionListeners.add(wrapped);
    // Emit current state right away
    wrapped(_connected);
    return () => _connectionListeners.delete(wrapped);
  }, [wsUrl]);
}
